import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTitle } from '@/lib/useTitle'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const DEFAULT_SETTINGS: Record<string, string> = {
  company_name: 'Velink SAS',
  company_address: '',
  company_siret: '',
  company_email: 'contact@velink.fr',
}

function useSettings() {
  const [settings, setSettings] = useState<Record<string, string>>(DEFAULT_SETTINGS)

  useEffect(() => {
    fetch(`${API}/api/public/settings`)
      .then((res) => {
        if (!res.ok) throw new Error()
        return res.json()
      })
      .then((data) => setSettings((prev) => ({ ...prev, ...data })))
      .catch(() => {})
  }, [])

  return settings
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-4">
      <dt className="text-text-muted text-sm w-48 flex-shrink-0">{label}</dt>
      <dd className="text-text-primary text-sm font-medium">{value}</dd>
    </div>
  )
}

export default function MentionsLegalesPage() {
  useTitle('Mentions légales')
  const s = useSettings()

  const COMPANY_NAME    = s.company_name    || 'Velink SAS'
  const COMPANY_ADDRESS = s.company_address || 'Adresse en cours de définition'
  const COMPANY_SIRET   = s.company_siret   || 'En cours d\'immatriculation'
  const COMPANY_EMAIL   = s.company_email   || 'contact@velink.fr'

  return (
    <div className="min-h-screen">
      {/* Navbar */}
      <header className="glass-navbar fixed top-0 inset-x-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/20 border border-accent/30 flex items-center justify-center">
              <span className="text-accent font-bold text-sm">V</span>
            </div>
            <span className="font-semibold text-text-primary text-lg tracking-tight">Velink</span>
          </Link>
          <Link to="/" className="glass-btn px-4 py-2 text-sm">← Retour à l'accueil</Link>
        </div>
      </header>

      <main className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto space-y-10">

          <div>
            <h1 className="text-3xl font-bold text-text-primary mb-2">Mentions légales</h1>
            <p className="text-text-muted text-sm">Conformément à la loi n° 2004-575 du 21 juin 2004 pour la Confiance dans l'Économie Numérique (LCEN).</p>
          </div>

          {/* 1. Éditeur */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">1. Éditeur du site</h2>
            <dl className="space-y-2">
              <Row label="Raison sociale" value={COMPANY_NAME} />
              <Row label="Forme juridique" value="Société par Actions Simplifiée (SAS)" />
              <Row label="Siège social" value={COMPANY_ADDRESS} />
              <Row label="SIRET" value={COMPANY_SIRET} />
              <Row label="Email de contact" value={COMPANY_EMAIL} />
            </dl>
          </div>

          {/* 2. Directeur de publication */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">2. Directeur de la publication</h2>
            <p className="text-text-secondary text-sm">
              Le directeur de la publication est le représentant légal de {COMPANY_NAME}.
              Pour toute question : <a href={`mailto:${COMPANY_EMAIL}`} className="text-accent hover:underline">{COMPANY_EMAIL}</a>
            </p>
          </div>

          {/* 3. Hébergement */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">3. Hébergement</h2>
            <dl className="space-y-2">
              <Row label="Hébergeur" value="Hostinger International Ltd" />
              <Row label="Adresse" value="61 Lordou Vironos Street, 6023 Larnaca, Chypre" />
              <Row label="Site" value="www.hostinger.fr" />
            </dl>
            <p className="text-text-muted text-xs mt-2">
              Les serveurs sont situés en Europe (UE) conformément au RGPD.
            </p>
          </div>

          {/* 4. Propriété intellectuelle */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">4. Propriété intellectuelle</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              L'ensemble du contenu de ce site (textes, images, logos, icônes, interface graphique, code source)
              est protégé par le droit d'auteur et appartient exclusivement à {COMPANY_NAME} ou à ses partenaires.
              Toute reproduction, représentation, modification, publication ou adaptation, totale ou partielle,
              est interdite sans l'autorisation écrite préalable de {COMPANY_NAME}.
            </p>
          </div>

          {/* 5. Données personnelles */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">5. Protection des données personnelles (RGPD)</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              {COMPANY_NAME} collecte et traite des données personnelles dans le cadre de son activité de mise en relation.
              Conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la loi Informatique
              et Libertés, vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside text-text-secondary text-sm space-y-1 ml-2">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement («&nbsp;droit à l'oubli&nbsp;»)</li>
              <li>Droit à la portabilité</li>
              <li>Droit d'opposition au traitement</li>
            </ul>
            <p className="text-text-secondary text-sm">
              Pour exercer ces droits, contactez-nous à : <a href={`mailto:${COMPANY_EMAIL}`} className="text-accent hover:underline">{COMPANY_EMAIL}</a>.
              Vous pouvez également introduire une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.cnil.fr</a>).
            </p>
          </div>

          {/* 6. Cookies */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">6. Cookies</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              Ce site utilise uniquement des cookies techniques strictement nécessaires à son fonctionnement
              (session utilisateur, préférences). Aucun cookie publicitaire ou de tracking tiers n'est déposé
              sans votre consentement.
            </p>
          </div>

          {/* 7. Responsabilité */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">7. Limitation de responsabilité</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              {COMPANY_NAME} agit en qualité d'intermédiaire de mise en relation et ne peut être tenu responsable
              des dommages directs ou indirects résultant de l'utilisation du site ou des services fournis
              par les prestataires référencés sur la plateforme.
            </p>
          </div>

          {/* 8. Loi applicable */}
          <div className="glass-card p-6 rounded-2xl space-y-4">
            <h2 className="text-base font-semibold text-text-primary">8. Droit applicable</h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux
              compétents seront ceux du ressort du siège social de {COMPANY_NAME}, sauf disposition légale contraire.
            </p>
          </div>

          <p className="text-text-muted text-xs text-center">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </main>
    </div>
  )
}
